import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

// 请求配置接口
export interface RequestConfig extends AxiosRequestConfig {
  showError?: boolean; // 是否显示错误信息
  retry?: number; // 重试次数
  retryDelay?: number; // 重试延迟
}

// 响应数据接口
export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
  success: boolean;
}

// 错误信息接口
export interface RequestError {
  code: number;
  message: string;
  details?: any;
}

// 请求拦截器配置
export interface RequestInterceptor {
  onRequest?: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;
  onRequestError?: (error: AxiosError) => Promise<AxiosError>;
}

// 响应拦截器配置
export interface ResponseInterceptor {
  onResponse?: <T>(response: AxiosResponse<ApiResponse<T>>) => AxiosResponse<ApiResponse<T>> | Promise<AxiosResponse<ApiResponse<T>>>;
  onResponseError?: (error: AxiosError) => Promise<AxiosError>;
}

// 创建请求实例的配置
export interface CreateRequestConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
  requestInterceptor?: RequestInterceptor;
  responseInterceptor?: ResponseInterceptor;
  onError?: (error: RequestError) => void;
}

/**
 * 处理响应错误
 */
function handleResponseError(error: AxiosError): RequestError {
  if (error.response) {
    // 服务器返回错误状态码
    const { status, data } = error.response;
    return {
      code: status,
      message: (data as any)?.message || `HTTP ${status} 错误`,
      details: data,
    };
  }
  if (error.request) {
    // 请求已发出但没有收到响应
    return {
      code: -1,
      message: '网络连接失败，请检查网络设置',
      details: error.request,
    };
  }
  // 请求配置错误
  return {
    code: -2,
    message: error.message || '请求配置错误',
    details: error,
  };
}

/**
 * 创建请求实例
 */
export function createRequest(config: CreateRequestConfig = {}): AxiosInstance {
  const {
    baseURL = '',
    timeout = 10000,
    headers = {},
    requestInterceptor,
    responseInterceptor,
    onError,
  } = config;

  // 创建 axios 实例
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });

  // 请求拦截器
  instance.interceptors.request.use(
    async (requestConfig) => {
      // 添加时间戳防止缓存
      if (requestConfig.method === 'get') {
        requestConfig.params = {
          ...requestConfig.params,
          _t: Date.now(),
        };
      }

      // 自定义请求拦截器
      if (requestInterceptor?.onRequest) {
        return requestInterceptor.onRequest(requestConfig);
      }

      return requestConfig;
    },
    async (error) => {
      // 自定义请求错误拦截器
      if (requestInterceptor?.onRequestError) {
        return requestInterceptor.onRequestError(error);
      }
      return Promise.reject(error);
    },
  );

  // 响应拦截器
  instance.interceptors.response.use(
    async (response) => {
      // 自定义响应拦截器
      if (responseInterceptor?.onResponse) {
        return responseInterceptor.onResponse(response);
      }
      return response;
    },
    async (error: AxiosError) => {
      const requestError = handleResponseError(error);

      // 调用错误回调
      if (onError) {
        onError(requestError);
      }

      // 自定义响应错误拦截器
      if (responseInterceptor?.onResponseError) {
        return responseInterceptor.onResponseError(error);
      }

      return Promise.reject(requestError);
    },
  );

  return instance;
}

/**
 * 重试机制
 */
async function retryRequest<T>(
  requestFn: () => Promise<T>,
  retryCount: number,
  retryDelay: number,
): Promise<T> {
  try {
    return await requestFn();
  } catch (error) {
    if (retryCount > 0) {
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      return retryRequest(requestFn, retryCount - 1, retryDelay);
    }
    throw error;
  }
}

/**
 * 通用 GET 请求
 */
export async function get<T = any>(
  url: string,
  params?: any,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  const { retry = 0, retryDelay = 1000, ...axiosConfig } = config || {};

  const requestFn = () => createRequest().get<ApiResponse<T>>(url, {
    params,
    ...axiosConfig,
  });

  const response = await retryRequest(requestFn, retry, retryDelay);
  return response.data;
}

/**
 * 通用 POST 请求
 */
export async function post<T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  const { retry = 0, retryDelay = 1000, ...axiosConfig } = config || {};

  const requestFn = () => createRequest().post<ApiResponse<T>>(url, data, axiosConfig);

  const response = await retryRequest(requestFn, retry, retryDelay);
  return response.data;
}

/**
 * 通用 PUT 请求
 */
export async function put<T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  const { retry = 0, retryDelay = 1000, ...axiosConfig } = config || {};

  const requestFn = () => createRequest().put<ApiResponse<T>>(url, data, axiosConfig);

  const response = await retryRequest(requestFn, retry, retryDelay);
  return response.data;
}

/**
 * 通用 DELETE 请求
 */
export async function del<T = any>(
  url: string,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  const { retry = 0, retryDelay = 1000, ...axiosConfig } = config || {};

  const requestFn = () => createRequest().delete<ApiResponse<T>>(url, axiosConfig);

  const response = await retryRequest(requestFn, retry, retryDelay);
  return response.data;
}

/**
 * 通用 PATCH 请求
 */
export async function patch<T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  const { retry = 0, retryDelay = 1000, ...axiosConfig } = config || {};

  const requestFn = () => createRequest().patch<ApiResponse<T>>(url, data, axiosConfig);

  const response = await retryRequest(requestFn, retry, retryDelay);
  return response.data;
}

/**
 * 文件上传
 */
export async function upload<T = any>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
  config?: RequestConfig,
): Promise<ApiResponse<T>> {
  const formData = new FormData();
  formData.append('file', file);

  const { retry = 0, retryDelay = 1000, ...axiosConfig } = config || {};

  const requestFn = () => createRequest().post<ApiResponse<T>>(url, formData, {
    ...axiosConfig,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...axiosConfig.headers,
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });

  const response = await retryRequest(requestFn, retry, retryDelay);
  return response.data;
}

/**
 * 下载文件
 */
export async function download(
  url: string,
  filename?: string,
  config?: RequestConfig,
): Promise<void> {
  const { retry = 0, retryDelay = 1000, ...axiosConfig } = config || {};

  const requestFn = async () => {
    const response = await createRequest().get(url, {
      ...axiosConfig,
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  };

  await retryRequest(requestFn, retry, retryDelay);
}

// 默认请求实例
export const request = createRequest();

// 导出所有方法
export default {
  createRequest,
  get,
  post,
  put,
  del,
  patch,
  upload,
  download,
  request,
};
